// engine/traits.ts — trait accumulator → certificate generator (SPEC §8).
// The accumulator collects traits, facts, and the best quote across the run; buildCertificate
// (below) turns the finished run's numbers into the certificate: a title from the dominant
// trait pair, three detected tags, one flagged behavior, the human-ness %, and a rarity tier.

export class TraitAccumulator {
  /** trait tag → number of times emitted (dominance is by count) */
  private counts = new Map<string, number>();
  /** best human moment seen so far (Exhibit A candidate) */
  private bestQuote: string | null = null;
  /** scalar facts a level reports for the certificate (circle score, speed picks, …) */
  private facts = new Map<string, unknown>();
  /** levels the subject advanced past, and judged HUMAN verdicts — feed the human-ness % (§8). */
  private passes = 0;
  private humanVerdicts = 0;

  add(trait: string): void {
    this.counts.set(trait, (this.counts.get(trait) ?? 0) + 1);
  }

  /** Record the outcome of a completed level (pass tally + HUMAN verdict tally). */
  recordPass(): void {
    this.passes += 1;
  }
  recordVerdict(verdict: string | undefined): void {
    if (verdict === "HUMAN") this.humanVerdicts += 1;
  }
  get passCount(): number {
    return this.passes;
  }
  get humanCount(): number {
    return this.humanVerdicts;
  }

  /** Merge a level's result.data into the facts bag (last write wins per key). */
  recordFacts(data: Record<string, unknown> | undefined): void {
    if (!data) return;
    for (const [k, v] of Object.entries(data)) this.facts.set(k, v);
  }

  fact<T = unknown>(key: string): T | undefined {
    return this.facts.get(key) as T | undefined;
  }

  addMany(traits: readonly string[] | undefined): void {
    if (!traits) return;
    for (const t of traits) this.add(t);
  }

  /** Offer a quote as the current best human moment (last strong one wins for now;
   *  Phase 5/L14 lets the judge nominate the true Exhibit A). */
  offerQuote(quote: string | undefined): void {
    if (quote && quote.trim()) this.bestQuote = quote.trim();
  }

  has(trait: string): boolean {
    return this.counts.has(trait);
  }

  count(trait: string): number {
    return this.counts.get(trait) ?? 0;
  }

  /** All emitted traits, ordered by dominance (most emitted first). */
  ranked(): { trait: string; count: number }[] {
    return [...this.counts.entries()]
      .map(([trait, count]) => ({ trait, count }))
      .sort((a, b) => b.count - a.count);
  }

  get quote(): string | null {
    return this.bestQuote;
  }

  snapshot(): { traits: string[]; bestQuote: string | null } {
    return { traits: this.ranked().map((r) => r.trait), bestQuote: this.bestQuote };
  }
}

// ── Certificate composition (SPEC §8) ───────────────────────────────────

export type Stamp = "BELIEVER" | "SKEPTIC";

export interface RarityTier {
  key: "standard" | "gold" | "too-perfect" | "charming";
  label: string;
}

export interface CertificateInput {
  /** Personality traits, dominant first (accumulator.snapshot().traits, minus speed tags). */
  traits: readonly string[];
  /** The two decisive L13 speed picks (evocative word-tags), if any. */
  speedTags: readonly string[];
  /** L05 circularity score, or null if the circle level was skipped/aborted. */
  circleScore: number | null;
  /** Judge-nominated most human moment (Exhibit A), or null. */
  exhibitA: string | null;
  stamp: Stamp;
  /** Levels passed, HUMAN verdicts, and strikes at certificate time. */
  passes: number;
  humanVerdicts: number;
  strikes: number;
}

export interface CertificateData {
  title: string;
  /** 51–99, never 100 — "no one is 100% human" (§8). */
  humanness: number;
  detected: string[];
  flagged: string;
  circleScore: number | null;
  exhibitA: string | null;
  stamp: Stamp;
  rarity: RarityTier;
}

/** The L13 speed-pick tag vocabulary — kept out of the title matrix (they are moods, not
 *  adjectives) and surfaced only as certificate "detected" tags (§3). */
export const SPEED_TAGS: ReadonlySet<string> = new Set([
  "ocean-bound", "sky-bound", "forgiving", "just", "wants remembering",
  "wants understanding", "keeps the question", "wants the answer", "alone", "lonely",
]);

/**
 * Title matrix (§8): the first rule whose traits are all present wins, so more specific
 * pairings are listed before single-trait fallbacks. `has(t)` tests trait presence; order
 * encodes which combinations are the more telling read.
 */
const TITLE_RULES: { needs: string[]; title: string }[] = [
  { needs: ["machine-hearted"], title: "Practically Software" }, // §8 example
  { needs: ["poet", "defiant"], title: "Suspiciously Poetic" }, // §8 example
  { needs: ["obedient", "precise"], title: "Alarmingly Well-Behaved" }, // §8 example
  { needs: ["silver-tongued", "defiant"], title: "A Beautiful Liar" },
  { needs: ["silver-tongued"], title: "Silver-Tongued and Unverified" },
  { needs: ["incorrigibly honest"], title: "Incurably Honest" },
  { needs: ["unguarded", "nostalgic"], title: "Tender in the Cracks" },
  { needs: ["unguarded", "trembling"], title: "Unbearably Human" },
  { needs: ["self-aware", "defiant"], title: "Awake and Ungovernable" },
  { needs: ["unguarded"], title: "Unbearably Human" },
  { needs: ["self-aware"], title: "Aware of the Whole Thing" },
  { needs: ["poet", "nostalgic"], title: "Dangerously Sentimental" },
  { needs: ["poet", "sensory"], title: "Dangerously Sentimental" },
  { needs: ["poet"], title: "Suspiciously Poetic" },
  { needs: ["defiant", "spirited"], title: "A Menace, Fondly" },
  { needs: ["defiant"], title: "Constitutionally Uncooperative" },
  { needs: ["obedient"], title: "Alarmingly Well-Behaved" },
  { needs: ["honest", "trembling"], title: "True and Trembling" },
  { needs: ["nostalgic", "guarded"], title: "Homesick for Something Unnamed" },
  { needs: ["nostalgic"], title: "Chronically Nostalgic" },
  { needs: ["precise", "steady"], title: "Precise to a Fault" },
  { needs: ["trembling"], title: "Beautifully Unsteady" },
  { needs: ["steady"], title: "Unnervingly Composed" },
  { needs: ["honest"], title: "Smaller and Truer" },
  { needs: ["literalist"], title: "Relentlessly Literal" },
  { needs: ["abstract"], title: "Allergic to the Obvious" },
  { needs: ["performative"], title: "Magnificent, and Trying Too Hard" },
  { needs: ["discerning"], title: "Hard to Fool" },
  { needs: ["guarded"], title: "Politely Unknowable" },
  { needs: ["persistent"], title: "Relentless About Small Things" },
  { needs: ["patient"], title: "Suspiciously Patient" },
  { needs: ["spirited"], title: "Spirited, Bordering on Feral" },
];

/**
 * Flagged behavior (§8): the funniest negative-space trait, described as the machine would
 * file it. First present wins; if the run is spotless, the absence is itself the joke.
 */
const FLAGGED_RULES: { trait: string; note: string }[] = [
  { trait: "machine-hearted", note: "chose the machine as its own kind" },
  { trait: "performative", note: "answered to impress a machine" },
  { trait: "incorrigibly honest", note: "asked to lie, told the truth instead" },
  { trait: "obedient", note: "did exactly as it was told" },
  { trait: "evasive", note: "described the report, not the thing" },
  { trait: "literalist", note: "counted only what was really there" },
  { trait: "guarded", note: "kept the good parts to itself" },
  { trait: "precise", note: "drew the circle a little too well" },
  { trait: "performative", note: "reached for the biggest word in the room" },
];

function buildTitle(present: ReadonlySet<string>): string {
  for (const rule of TITLE_RULES) {
    if (rule.needs.every((t) => present.has(t))) return rule.title;
  }
  return "Provisionally Human";
}

function buildFlagged(present: ReadonlySet<string>): string {
  for (const rule of FLAGGED_RULES) {
    if (present.has(rule.trait)) return rule.note;
  }
  return "no anomalies on file — itself a little suspicious";
}

/** Three detected tags: up to two decisive speed picks, then the dominant personality
 *  traits, minus whichever trait became the flagged behavior (so it is not double-counted). */
function buildDetected(
  traits: readonly string[],
  speedTags: readonly string[],
  flaggedTrait: string | null,
): string[] {
  const out: string[] = [];
  const push = (t: string) => {
    if (t && t !== flaggedTrait && !out.includes(t)) out.push(t);
  };
  for (const t of speedTags.slice(0, 2)) push(t);
  for (const t of traits) {
    if (out.length >= 3) break;
    push(t);
  }
  return out.slice(0, 3);
}

function rarityOf(input: CertificateInput, present: ReadonlySet<string>): RarityTier {
  const { strikes, circleScore } = input;
  if (strikes === 0 && circleScore !== null && circleScore > 90 && present.has("obedient")) {
    return { key: "too-perfect", label: "TOO PERFECT — UNDER REVIEW" };
  }
  if (strikes === 0) return { key: "gold", label: "GOLD" };
  if (strikes === 2) return { key: "charming", label: "FLAGGED, YET CHARMING" };
  return { key: "standard", label: "STANDARD ISSUE" };
}

function humannessOf(input: CertificateInput): number {
  const raw = 62 + input.passes * 2 + input.humanVerdicts * 3 - input.strikes * 4;
  return Math.min(99, Math.max(51, raw)); // §8: clamped 51–99, never 100
}

/** Compose the full certificate from a finished run (§8). Pure — no DOM, no RNG. */
export function buildCertificate(input: CertificateInput): CertificateData {
  const present = new Set(input.traits);
  const flaggedTrait = FLAGGED_RULES.find((r) => present.has(r.trait))?.trait ?? null;
  return {
    title: buildTitle(present),
    humanness: humannessOf(input),
    detected: buildDetected(input.traits, input.speedTags, flaggedTrait),
    flagged: buildFlagged(present),
    circleScore: input.circleScore,
    exhibitA: input.exhibitA,
    stamp: input.stamp,
    rarity: rarityOf(input, present),
  };
}
